import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { ICropImageData, ICropImageObject, ICropperOptions, IImageData, ITranslateProps } from 'models/common';
import { Button, EllipseGreyBlackButton, WhiteButton } from 'components/Common/Button';
import { Field } from 'redux-form';
import { Modal } from 'components/Modal';
import { ImageUpload } from '../ImageUpload';
// import { IOnboardingQuizFormData } from 'models/onboardingquiz';

export enum ImageState {
	wait,
	ready,
}

interface IOwnProps {
	imageId?: string;
	imageUrl?: string;
	imageCrop?: ICropImageObject;
	onChangeCropData: (cropData: ICropImageObject) => any;
	onChangeImageData: (data: IImageData) => Promise<any>;
	onConfirm?: () => any;
	title?: string;
	onImgStateChange?: (state: ImageState) => void;
	currentFormValues?: any;
}

interface IState {
	imageData: IImageData;
	editingPicture: boolean;
	originalImage: {
		width: number;
		height: number;
	};
	scaledImage: {
		widthBig: number;
		heightBig: number;
		widthPerc: number;
		heightPerc: number;
	};
	error: string;
}

interface IProps extends ITranslateProps, IOwnProps {}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class NewImageUpload extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.imageUploadedCallback = this.imageUploadedCallback.bind(this);
		this.imageUploadFailed = this.imageUploadFailed.bind(this);
		this.setCroppedImage = this.setCroppedImage.bind(this);
		this.handleUploadButtonClick = this.handleUploadButtonClick.bind(this);
		this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
		this.hideModal = this.hideModal.bind(this);
		this.deleteImage = this.deleteImage.bind(this);
		this.handleOnConfirm = this.handleOnConfirm.bind(this);
		this.onFileDialogCancel = this.onFileDialogCancel.bind(this);

		this.state = {
			editingPicture: false,
			imageData: {
				id: null,
				href: null,
			},
			originalImage: {
				width: 0,
				height: 0,
			},
			scaledImage: {
				widthBig: 0,
				heightBig: 0,
				widthPerc: 0,
				heightPerc: 0,
			},
			error: '',
		};
	}

	imageInputRef = React.createRef();

	public handleUploadButtonClick() {
		if (this.imageInputRef?.current) {
			(this.imageInputRef.current as any).open();
		}

		if (this.props.onImgStateChange) this.props.onImgStateChange(ImageState.wait);
	}

	public handleEditButtonClick() {
		const id = this.props.currentFormValues?.id; // from uncropped image
		const url = this.props.currentFormValues?.url; // from uncropped image
		const { imageData } = this.state;
		if (!imageData.id && !imageData.href && !!id && !!url) {
			this.setState({ editingPicture: true, imageData: { href: url, id: id } });
		} else {
			this.setState({ editingPicture: true });
		}
		if (this.props.onImgStateChange) this.props.onImgStateChange(ImageState.wait);
	}

	public hideModal() {
		this.setState({ editingPicture: false });
		if (this.props.onImgStateChange) this.props.onImgStateChange(ImageState.ready);
	}

	private deleteImage() {
		this.props
			.onChangeImageData({
				id: null,
				href: null,
			})
			.then(() => {
				this.props.onChangeCropData(null);
				if (this.props.onImgStateChange) this.props.onImgStateChange(ImageState.ready);
			});
	}

	private setCroppedImage(cropData: ICropImageObject) {
		const { onChangeCropData, imageCrop } = this.props;
		Promise.all([onChangeCropData(cropData)]).then(() => {
			const a = document.querySelector('.cropper-hidden') as HTMLImageElement;
			if (!a) {
				return;
			}
			const imageSrc = a.src;

			const image = new Image();
			image.src = imageSrc;

			image.onload = () => {
				const width = image.width;
				const height = image.height;
				const cropWidth = imageCrop?.width || 100;
				const cropHeight = imageCrop?.height || 100;
				const xPercentageToScale = (140 * 100) / cropWidth;
				const yPercentageToScale = (140 * 100) / cropHeight;

				this.setState({
					originalImage: {
						width,
						height,
					},
					scaledImage: {
						widthBig: (xPercentageToScale / 100) * width,
						heightBig: (yPercentageToScale / 100) * height,
						widthPerc: xPercentageToScale,
						heightPerc: yPercentageToScale,
					},
				});
			};
		});
	}

	private imageUploadFailed(error: any) {
		if (error) {
			this.setState({ error: error && error.error ? error.error[0] : '' });
		}
	}

	private imageUploadedCallback(id: string, href: string) {
		const image: IImageData = {
			id,
			href,
		};
		this.props.onChangeImageData(image).then(() => {
			this.setState({
				editingPicture: true,
				imageData: image,
				error: '',
			});
		});
	}

	private onFileDialogCancel() {
		if (this.props.onImgStateChange) this.props.onImgStateChange(ImageState.ready);
	}

	private handleOnConfirm() {
		this.props.onChangeImageData(this.state.imageData).then(() => {
			if (this.props.onConfirm) {
				this.props.onConfirm();
			}
			this.hideModal();
		});
	}

	public render() {
		const { t, imageUrl } = this.props;
		const { editingPicture, imageData } = this.state;
		// console.log(imageData, imageUrl, imageId)
		const imageCropOptions: ICropperOptions = {
			...c.DEFAULT_CROP_OPTIONS,
			aspectRatio: c.ASPECT_RATION_SHOP_PROFILE_PICTURE,
			croppedImageCallback: this.setCroppedImage,
			showSlider: true,
			minContainerHeight: 300,
			minContainerWidth: 300,
			rotatable: true,
		};

		const clickableElement = (text: string, action: (image?: ICropImageData) => void): JSX.Element => {
			return <EllipseGreyBlackButton onClick={() => action()}>{text}</EllipseGreyBlackButton>;
		};
		const subtext = `JPEG, PNG (${t('User_UserCard_Optimal')}: ${
			c.DEFAULT_CROP_SIZES_PROFILE_IMAGE[0].thumbnailWidth +
			' x ' +
			c.DEFAULT_CROP_SIZES_PROFILE_IMAGE[0].thumbnailHeight
		}, < 4MB)`;
		const modalFooter = (
			<div className="modalButtonContainer">
				<WhiteButton onClick={this.hideModal}>{t('BSPortal_Common_Cancel')}</WhiteButton>
				<Button onClick={this.handleOnConfirm}>{t('BSPortal_Common_Save')}</Button>
			</div>
		);

		return (
			<div className="newPictureUpload">
				{imageUrl || imageData?.href ? (
					<div
						style={{
							backgroundImage: `url(${imageUrl ? imageUrl : imageData?.href})`,
							backgroundSize: `cover`,
							backgroundPositionX: `center center`,
						}}
						className="profilePicture"
					/>
				) : null}
				<div>
					<div className="buttonContainer">
						{clickableElement(t('User_UserCard_UploadNewPicture'), this.handleUploadButtonClick)}
						{imageUrl || imageData?.href
							? clickableElement(t('User_UserCard_EditCurrentPicture'), this.handleEditButtonClick)
							: null}
					</div>
					<p className="grayGigo">{subtext}</p>
					<Modal
						modalClass={`newPictureUploadModal ${editingPicture ? 'visible' : ''}`}
						title={t('User_UserCard_EditCurrentPicture')}
						closeModal={this.hideModal}
						size="small"
						footer={modalFooter}
					>
						<div className="card">
							<Field
								className="imageUpload"
								name="imageUpload1"
								dropZoneRef={this.imageInputRef}
								component={ImageUpload}
								cropImage={true}
								cropOptions={imageCropOptions}
								imageUploadedCallback={this.imageUploadedCallback}
								imageUploadFailed={this.imageUploadFailed}
								existingImageId={imageData?.id || this.props.currentFormValues?.id}
								existingImageUrl={imageData?.href || this.props.currentFormValues?.href}
								onDelete={this.deleteImage}
								onFileDialogCancel={this.onFileDialogCancel}
								forceKeepDropzone={true}
								maxImageSize={10000000}
							/>
						</div>
					</Modal>
					<p className="p14 red">{this.state.error}</p>
				</div>
			</div>
		);
	}
}

export { NewImageUpload };
